import _ from 'lodash'

import { evaluateFormula } from './evaluation'
import { Query } from '../models/query'

/**
 * Resolves where a select/tags field's dropdown options come from. A field
 * opts in by declaring `optionSource` on its schema property; the resolver then
 * supplies options at runtime — no schema deploy needed to change them. Sources
 * (in precedence order):
 *
 *   1. `referenceList: '<key>'` — a firm-owned controlled vocabulary
 *      (`core_config_referenceList` entity holding a `key` + `items` array),
 *      resolved against the entity's owner firm. This is the "theming" list a
 *      firm admin manages in-app.
 *   2. `distinctValues: true` — the distinct values already stored on the bound
 *      field across the firm's entities (grouped query), so the field
 *      self-suggests from prior usage. Used as the fallback when no reference
 *      list is configured.
 *   3. The field's own static schema `enum`/`suggestions` (handled by the
 *      caller / select factory).
 *
 * This module is platform-agnostic (web + mobile). The React hook lives in the
 * webapp; mobile calls these functions directly.
 *
 * See schemas/entities/config/referenceList.json for the reference-list entity.
 */

export const REFERENCE_LIST_ENTITY_TYPE = '/1.0/entities/metadata/core_config_referenceList.json'
const KEY_PATH = 'core_config_referenceList.key'
const ITEMS_PATH = 'core_config_referenceList.items'
const OWNER_FIRM_ID_PATH = 'owner.firm.entityId'
const DEFAULT_DISTINCT_VALUES_SIZE = 100

export interface IReferenceListOption {
  label: string
  value: string
}

export interface IOptionSource {
  /** Key of a firm-owned reference list to resolve options from. */
  referenceList?: string
  /** When true, suggest the distinct values already stored on the bound field. */
  distinctValues?: boolean
}

/**
 * Reads the `optionSource` for a field, whether it arrived as an explicit prop
 * or (the common case) on the bound schema property surfaced via the
 * `dataSchema` frames context. For an array field, `optionSource` sits on the
 * array node itself.
 */
export const getOptionSource = (props: { optionSource?: IOptionSource; frames?: any }): IOptionSource | undefined => {
  if (props?.optionSource) {
    return props.optionSource
  }
  const dataSchema = props?.frames?.getContext?.('dataSchema')
  return dataSchema?.optionSource
}

/** Convenience: the reference-list key of a field's optionSource, if any. */
export const getReferenceListKey = (props: { optionSource?: IOptionSource; frames?: any }): string | undefined =>
  getOptionSource(props)?.referenceList

/** Resolves the entity-type id for the entity a field is bound to (for value queries). */
export const resolveEntityTypeId = (entity: any, entityType: string | undefined, valuePath: string[]): string => {
  if (entityType) {
    return entityType
  }
  const namespace = _.first(valuePath)
  const schema = _.find(entity?.schemas, (s) => _.get(s, 'metadata.namespace') === namespace)
  return schema?.id ?? `/1.0/entities/metadata/${namespace}.json`
}

/**
 * The firm that OWNS the entity being edited. The reference list is resolved
 * against this firm — not the acting session's firm — so a cross-firm switcher
 * (e.g. a support user in another firm editing this firm's trailer) still sees
 * the owner firm's vocabulary. Undefined for a brand-new entity with no owner
 * yet, in which case the query falls back to the ambient session firm.
 */
export const getEntityOwnerFirmId = (entity: any): string | undefined =>
  entity?.get?.(OWNER_FIRM_ID_PATH) ?? _.get(entity, OWNER_FIRM_ID_PATH)

/** Maps a reference list's raw items to sorted, active {value,label} options. */
export const mapReferenceListItemsToOptions = (items: any[]): IReferenceListOption[] =>
  _.chain(items)
    .filter((item) => item?.isActive !== false && _.isString(item?.value) && item.value !== '')
    .sortBy((item) => (_.isFinite(item?.sortOrder) ? item.sortOrder : Number.MAX_SAFE_INTEGER))
    .map((item) => ({
      value: item.value,
      label: _.isString(item?.label) && item.label !== '' ? item.label : item.value,
    }))
    .value()

/**
 * Loads a firm's reference list by key and returns its active items as
 * {value,label} options. When `firmId` is supplied the query is scoped to that
 * firm (the entity's owner firm); otherwise it runs under the authenticated
 * session and is auto-scoped to the caller's firm. Resolves to [] when no list
 * is configured, so callers can fall back to static suggestions / free-text.
 */
export const fetchReferenceListOptions = (
  apis: any,
  referenceListKey?: string,
  firmId?: string,
): Promise<IReferenceListOption[]> => {
  if (!referenceListKey) {
    return Promise.resolve([])
  }
  const query = new Query(apis)
    .setEntityType(REFERENCE_LIST_ENTITY_TYPE)
    .setFilters([{ type: 'match', path: KEY_PATH, value: referenceListKey }])
    .setSize(1)
    .setDebug({ context: 'referenceListOptions' })
  if (firmId) {
    query.setFirm(firmId)
  }
  return query
    .getCollection()
    .find()
    .then((entities: any[]) => mapReferenceListItemsToOptions(_.get(_.first(entities), ITEMS_PATH) ?? []))
    .catch(() => [])
}

/** Maps grouped-query buckets to distinct {value,label} options. */
export const mapDistinctBuckets = (buckets: any[]): IReferenceListOption[] =>
  _.compact(
    _.map(buckets, (bucket) => {
      const value = bucket?.data
      return _.isString(value) && value ? { label: value, value } : null
    })
  )

/**
 * Loads the distinct values already stored on a field across the firm's
 * entities (a grouped query on the bound field path), so a field can
 * self-suggest from prior usage. The query runs under the session firm.
 * Resolves to [] when there is no value path.
 *
 * The analytics/EQL path registry keys array-of-primitive fields under the bare
 * dotted path (e.g. `core_yms_trailer.tags`) with an internal isArray flag — no
 * `[*]` suffix (that suffix belongs to the separate search-schema path system).
 */
export const fetchDistinctFieldValues = (
  apis: any,
  entityType: string,
  valuePath: string[],
  size?: number,
): Promise<IReferenceListOption[]> => {
  if (_.isEmpty(valuePath) || !entityType) {
    return Promise.resolve([])
  }
  return new Query(apis)
    .setEntityType(entityType)
    .setGroups([{ path: valuePath.join('.'), type: 'field' }])
    .setShouldIncludeLeafEntities(false)
    .setSize(size ?? DEFAULT_DISTINCT_VALUES_SIZE)
    .setDebug({ context: 'optionSource--distinctValues' })
    .getCollection()
    .find()
    .then((buckets: any[]) => mapDistinctBuckets(buckets))
    .catch(() => [])
}

/**
 * Normalizes creatable multi-select values with a `transform` formula (e.g.
 * 'UPPER(TO_IDENTIFIER(arg0))') on change — deduped, with empties dropped. This
 * is the value normalization the retired `ui:tagsField` used to bundle; it now
 * applies to any select via the field's `transform` prop. Non-array values pass
 * through unchanged.
 */
export const applyTagTransform = (transform: string | undefined, values: any): any => {
  if (!transform || !_.isArray(values)) {
    return values
  }
  const next = _.uniq(_.map(values, (value) => (_.isString(value) ? evaluateFormula(transform, value) ?? value : value)))
  return _.reject(next, (value) => value === '')
}
